import { useLayoutEffect, useRef, type ReactNode } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import * as THREE from 'three';
import type { OrbitControls as OrbitControlsImpl } from 'three-stdlib';

const AZIMUTH_STEPS = 72;
const POLAR_EPS = 1e-3;
const POLAR_TOLERANCE = 2e-3;
const MIN_ZOOM = 0.45;
const MAX_ZOOM = 2;
const WORLD_UP = new THREE.Vector3(0, 1, 0);
const AZIMUTHS = Array.from({ length: AZIMUTH_STEPS }, (_, i) => (i / AZIMUTH_STEPS) * Math.PI * 2);

const box = new THREE.Box3();
const center = new THREE.Vector3();
const back = new THREE.Vector3();
const right = new THREE.Vector3();
const up = new THREE.Vector3();
const offset = new THREE.Vector3();
const spherical = new THREE.Spherical();

type Framing = { corners: THREE.Vector3[]; tanH: number; tanV: number; polar: number; fit: number };

function boxCorners(b: THREE.Box3, origin: THREE.Vector3): THREE.Vector3[] {
  return Array.from({ length: 8 }, (_, i) =>
    new THREE.Vector3(i & 1 ? b.max.x : b.min.x, i & 2 ? b.max.y : b.min.y, i & 4 ? b.max.z : b.min.z).sub(origin),
  );
}

function turntableDistance(corners: THREE.Vector3[], tanH: number, tanV: number, polar: number): number {
  const phi = THREE.MathUtils.clamp(polar, POLAR_EPS, Math.PI - POLAR_EPS);
  return AZIMUTHS.reduce((far, azimuth) => {
    back.setFromSphericalCoords(1, phi, azimuth);
    right.crossVectors(WORLD_UP, back).normalize();
    up.crossVectors(back, right);
    return corners.reduce((need, c) => {
      const depth = c.dot(back);
      return Math.max(need, depth + Math.abs(c.dot(right)) / tanH, depth + Math.abs(c.dot(up)) / tanV);
    }, far);
  }, 0);
}

function applyDistance(controls: OrbitControlsImpl, camera: THREE.Camera, framing: Framing, zoom: number) {
  offset.subVectors(camera.position, controls.target);
  spherical.setFromVector3(offset);
  spherical.radius = framing.fit * THREE.MathUtils.clamp(zoom, MIN_ZOOM, MAX_ZOOM);
  camera.position.setFromSpherical(spherical).add(controls.target);
  controls.minDistance = framing.fit * MIN_ZOOM;
  controls.maxDistance = framing.fit * MAX_ZOOM;
}

export function FitCamera({ children, margin = 1, refitKey }: { children: ReactNode; margin?: number; refitKey?: unknown }) {
  const group = useRef<THREE.Group>(null);
  const framing = useRef<Framing | null>(null);
  const camera = useThree((s) => s.camera);
  const controls = useThree((s) => s.controls) as OrbitControlsImpl | null;
  const width = useThree((s) => s.size.width);
  const height = useThree((s) => s.size.height);

  useLayoutEffect(() => {
    if (!(camera instanceof THREE.PerspectiveCamera)) throw new Error('FitCamera needs a perspective camera');
    const g = group.current;
    if (!g || !controls || width === 0 || height === 0) return;
    g.updateWorldMatrix(true, true);
    box.setFromObject(g);
    if (box.isEmpty()) return;
    box.getCenter(center);

    offset.subVectors(camera.position, controls.target);
    spherical.setFromVector3(offset);
    const previous = framing.current;
    const zoom = previous === null ? 1 : spherical.radius / previous.fit;

    const tanV = Math.tan(THREE.MathUtils.degToRad(camera.fov) / 2) / margin;
    const tanH = (tanV * width) / height;
    const corners = boxCorners(box, center);
    const next = { corners, tanH, tanV, polar: spherical.phi, fit: turntableDistance(corners, tanH, tanV, spherical.phi) };
    framing.current = next;

    controls.target.copy(center);
    camera.position.copy(center).add(offset);
    applyDistance(controls, camera, next, zoom);
    controls.update();
  }, [camera, controls, width, height, margin, refitKey]);

  useFrame(() => {
    const f = framing.current;
    if (!f || !controls) return;
    offset.subVectors(camera.position, controls.target);
    spherical.setFromVector3(offset);
    if (Math.abs(spherical.phi - f.polar) < POLAR_TOLERANCE) return;
    const zoom = spherical.radius / f.fit;
    f.polar = spherical.phi;
    f.fit = turntableDistance(f.corners, f.tanH, f.tanV, f.polar);
    applyDistance(controls, camera, f, zoom);
  });

  return <group ref={group}>{children}</group>;
}
